import { ApplicationRef, EnvironmentProviders, enableProdMode, importProvidersFrom, Injector, LOCALE_ID, inject, provideAppInitializer, DOCUMENT } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Router } from '@angular/router';
import { bootstrapApplication, BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PortalModule } from '@angular/cdk/portal';
import { IgoCoreModule } from '@igo2/core';
import { provideMessage } from '@igo2/core/message';
import { ConfigService, provideConfig } from '@igo2/core/config';
import { LanguageService, provideTranslation, withStaticConfig } from '@igo2/core/language';
import type { TranslationObject } from '@ngx-translate/core';
import { loadTheme, ObjectUtils } from '@igo2/utils';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { environmentConfigLoader } from './environments/environmentConfigLoader';
import { IntranetRoutes, LegereRoutes, PesRoutes, provideAppRouting } from './app/app.routes';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAuthentification } from '@igo2/auth';
import { provideIcon } from '@igo2/common/icon';
import { concatMap, first, from, lastValueFrom, switchMap } from 'rxjs';
import { RouteService } from '@igo2/core/route';
import { SecuriteInitService } from './app/zzz/securite/securite.init.service';
import { CoreInitService } from './app/zzz/core/core-init.service';
import 'hammerjs';
import localeFr from '@angular/common/locales/fr';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { SigtPaginatorTraduction } from './app/shared/paginator/sigt-paginator-traduction.service';
import { SigtConfigServiceService } from './app/shared/services/sigt-config-service.service';
import { IdzUnauthorizedInterceptor } from './app/zzz/securite/idz-unauthorized.interceptor';
import { ZzzSecuriteService } from './app/zzz/securite/shared/securite.service';
import { NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { IgoInteractiveTourModule } from '@igo2/common/interactive-tour';
import { provideTranslationSigt, withStaticConfigSigt } from './app/shared/language/provideTranslationSigt';

registerLocaleData(localeFr);

const DEFAULT_THEME = 'blue-theme';

const TOOLTIP_OPTIONS: MatTooltipDefaultOptions = {
  showDelay: 500,
  hideDelay: 0,
  touchendHideDelay: 0,
  disableTooltipInteractivity: true
};
const igoIconNameMap: Record<string, string> = {
  plus: 'add',
  city: 'location_city'
};

class CustomDateAdapter extends NativeDateAdapter {
  format(date: Date, _displayFormat: any): string {
    const days = date.getDate();
    const months = date.getMonth() + 1;
    const year = date.getFullYear();
    return year + '-' +
      (months < 10 ? '0' : '') + months + '-' +
      (days < 10 ? '0' : '') + days;
  }
}

environmentConfigLoader.then(config => {
  if (config.enableProductionMode) {
    enableProdMode();
  }

  environment.config = config;
  environment.igo.auth.hostsWithCredentials = [
    {
      withCredentials: true,
      domainRegFilters: '(' + environment.config.authApiEndPoint + ')'
    }
  ];

  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        IgoCoreModule,
        PortalModule,
        IgoInteractiveTourModule
      ),
      provideHttpClient(withInterceptorsFromDi()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: IdzUnauthorizedInterceptor,
        deps: [ZzzSecuriteService],
        multi: true
      },
      provideMessage(),
      provideAnimations(),
      provideAppRouting(),
      provideAppRoutes(),
      provideAppInitializer(() => {
        const initializerFn = (provideSigtTranslation)(inject(Injector), inject(ApplicationRef));
        return initializerFn();
      }),
      provideAppInitializer(() => {
        const initializerFn = (provideSigtInitApp)(inject(SecuriteInitService), inject(CoreInitService));
        return initializerFn();
      }),
      {
        provide: LOCALE_ID,
        useValue: 'fr-CA'
      },
      provideTranslationSigt(withStaticConfigSigt({ prefix: './locale/' }, 'fr')),
      provideAuthentification(),
      provideIcon(),
      //provideTheme(),
      provideNgxMask(),
      NgxMaskPipe,
      {
        provide: DateAdapter,
        useClass: CustomDateAdapter,
        deps: [MAT_DATE_LOCALE, Platform]
      },
      RouteService,
      {
        provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
        useValue: TOOLTIP_OPTIONS
      },
      {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
          appearance: 'fill'
        }
      },
      {
        provide: MatPaginatorIntl,
        useClass: SigtPaginatorTraduction
      },
      {
        provide: ConfigService,
        useClass: SigtConfigServiceService
      },
      provideConfig({
        default: ObjectUtils.mergeDeep(environment.config, environment.igo),
        path: environment.config.authApiEndPoint + '/sigtapi/config'
      })
    ]
  }).then(() => {
    updateIgoIconError();
  }).catch(err => console.log(err));
});

function provideAppRoutes(): EnvironmentProviders {
  return provideAppInitializer(() => {
        const initializerFn = ((router: Router) => () => {
      if (environment.config.estIntranet) {
        router.resetConfig(IntranetRoutes);
      } else if (environment.config.estPartenaire) {
        router.resetConfig(PesRoutes);
      } else {
        router.resetConfig(LegereRoutes);
      }
    })(inject(Router));
        return initializerFn();
      })
}

function provideSigtInitApp(
  securityInitService: SecuriteInitService,
  coreInitService: CoreInitService
) {
  var obs = from(coreInitService.init(environment.config)).pipe(
    switchMap((_) => from(securityInitService.init()))
  );

  return () => lastValueFrom(obs);
}

function provideSigtTranslation(
  injector: Injector,
  applicationRef: ApplicationRef
) {
  // Recharge les traductions une fois l'application stable.
  //
  // Ne PAS retourner de promesse ici : cette fonction est un initialiseur
  // d'application, et Angular attend les initialiseurs AVANT de demarrer
  // l'application. Or applicationRef.isStable ne devient vrai qu'APRES le
  // demarrage. Attendre cette promesse bloquait donc le demarrage pour
  // toujours, sans la moindre erreur : l'application restait sur le splash.
  // On declenche le travail ici, et on laisse le demarrage se poursuivre.
  return () => {
    applicationRef.isStable
      .pipe(
        first((isStable) => isStable === true),
        concatMap(() => {
          const languageService = injector.get(LanguageService);
          languageService.setLanguage('fr');
          const lang = languageService.getLanguage();
          return languageService.translate.reloadLang(lang);
        })
      )
      .subscribe((translations) => {
        const languageService = injector.get(LanguageService);
        const lang = languageService.getLanguage();
        languageService.translate.setTranslation(lang, translations as unknown as TranslationObject);
      });
  };
}

function provideTheme(): EnvironmentProviders {
  return provideAppInitializer(() => {
        const initializerFn = ((configService: ConfigService, document: Document) => () =>
      configService.isLoaded$
        .pipe(first((isLoaded) => isLoaded))
        .subscribe(() => {
          const theme = configService.getConfig('theme', DEFAULT_THEME);
          loadTheme(document, theme);
        }))(inject(ConfigService), inject(DOCUMENT));
        initializerFn();
      });
}

function updateIgoIconError() {
  const observer = new MutationObserver(() => {
    const icons = document.querySelectorAll('mat-icon');
    icons.forEach(icon => {
      icon.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE) {
          const newName = igoIconNameMap[node.textContent?.trim() as keyof typeof igoIconNameMap];
          if (newName) {
            node.textContent = newName;
          }
        }
      });
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });
}

